import { useQueries } from '@tanstack/react-query'
import { useCallback } from 'react'

import { stringifyQueryString, useQuery, useQueryClient } from '@fv/client-core'
import type {
  CalendarLookupDTO,
  DateRange,
  LoadTrackingUpdateEvent,
  ShipmentUpdateEvent,
} from '@fv/models'
import type { DTO } from '@fv/models/core'

import { shipperFetch } from '@/utils/shipperFetch'

import type { CalendarShipmentSummary } from './types'

export const calendarQueryKeys = {
  all: ['calendar'] as const,
  range: (dateRange: DateRange) =>
    [...calendarQueryKeys.all, 'range', dateRange] as const,
  week: (filter: CalendarLookupDTO) =>
    [...calendarQueryKeys.all, filter] as const,
}

const fetchCalendarShipments = async (query: CalendarLookupDTO) => {
  return await shipperFetch<{ data: CalendarShipmentSummary[] }>(
    '/shipments/calendar',
    {
      query: stringifyQueryString(query),
    },
  )
}

export const useCalendarRange = (dateRange: DateRange) => {
  return useQuery({
    queryKey: calendarQueryKeys.range(dateRange),
    queryFn: () => fetchCalendarShipments({ dateRange, status: [] }),
  })
}

type WeekFilter = {
  weekKey: string
  filter: CalendarLookupDTO
}

export const useCalendarWeekShipments = (weekFilters: WeekFilter[]) => {
  return useQueries({
    queries: weekFilters.map(({ filter }) => ({
      queryKey: calendarQueryKeys.week(filter),
      queryFn: () => fetchCalendarShipments(filter),
    })),
    combine: queries => {
      const data = Array.from(
        new Map(
          queries.flatMap(
            weekQuery =>
              weekQuery.data?.data?.map(result => [result.loadId, result]) ??
              [],
          ),
        ).values(),
      ).flat()
      return {
        data,
        isLoading: queries.some(q => q.isLoading),
      }
    },
  })
}

export const useUpdateCalendarCache = () => {
  const queryClient = useQueryClient()
  return useCallback(
    (data: Partial<CalendarShipmentSummary>) => {
      const { loadId, ...rest } = data
      if (!loadId || !Object.keys(rest).length) return

      queryClient.setQueriesData<{ data: CalendarShipmentSummary[] }>(
        { queryKey: calendarQueryKeys.all },
        oldData => {
          if (!oldData) return oldData
          return {
            data: oldData.data.map(d =>
              d.loadId === loadId ? { ...d, ...rest } : d,
            ),
          }
        },
      )
    },
    [queryClient],
  )
}

export const useCalendarLiveEventsHandler = () => {
  const updateCache = useUpdateCalendarCache()

  const handleShipmentUpdate = useCallback(
    (event: ShipmentUpdateEvent) => {
      const { fields } = event.data as any
      if (fields.pickupDate || fields.bol?.bolNumber) {
        updateCache({
          loadId: event.data.loadId,
          ...(fields.pickupDate && { pickupDate: fields.pickupDate }),
          ...(fields?.bol?.bolNumber && {
            bolNumber: fields.bol.bolNumber,
          }),
        })
      }
    },
    [updateCache],
  )
  const handleTrackingUpdate = useCallback(
    (event: DTO<LoadTrackingUpdateEvent>) => {
      const { track } = event.data
      if (!track) return
      const { deliveryDateActual, deliveryDateEstimate, currentStatus } = track
      updateCache({
        loadId: event.data.loadId,
        ...(currentStatus && { trackingStatus: currentStatus }),
        ...((deliveryDateActual || deliveryDateEstimate) && {
          deliveryDate: deliveryDateActual || deliveryDateEstimate,
        }),
      })
    },
    [updateCache],
  )

  return useCallback(
    (event: DTO<ShipmentUpdateEvent | LoadTrackingUpdateEvent>) => {
      if (event.eventType === 'shipper:shipment:update') {
        handleShipmentUpdate(event)
      } else if (event.eventType === 'load:tracking:update') {
        handleTrackingUpdate(event)
      }
    },
    [handleShipmentUpdate, handleTrackingUpdate],
  )
}
